import React, { useState, useRef, useEffect } from 'react';
import {
	SquareCode,
	MessageSquare,
	ThumbsUp,
	ThumbsDown,
	Copy,
	Check,
	Database,
	RotateCw,
	ExternalLink,
} from 'lucide-react';
import { FiEdit2 } from 'react-icons/fi';
import { format } from 'sql-formatter';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LoadingIndicator from './loadingIndicator';
import { Visualization } from './visualizations';
import { ChatMessage } from 'models/ai-chat/chat.types';
import ChatDrawer from './chat-drawer';
import UnderlyingDataComponent from './underlying-data-component';
import MessageContent from './message-content';
import Tooltip from './tooltip';
import ChartSwitcher from './visualizations/ChartSwitcher';
import AppliedFiltersDisplay from './visualizations/AppliedFiltersDisplay';
import { FilterItem } from 'models/ai-chat/filter-request';
import { TableViewRef } from './visualizations/TableView';
import { useChat } from 'store/useChatStore';

const animationStyles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px) scaleY(0.95);
      max-height: 0;
    }
    to {
      opacity: 1;
      transform: translateY(0) scaleY(1);
      max-height: 500px;
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0) scaleY(1);
      max-height: 500px;
    }
    to {
      opacity: 0;
      transform: translateY(-10px) scaleY(0.95);
      max-height: 0;
    }
  }
  
  .insight-content p {
    margin: 0 !important;
    line-height: 1.6;
  }

  .space-y-1 p, {
   margin: 0 !important;
   line-height: 1.6;
  }

  .insight-content span[style*="font-weight:600"] {
    font-weight: 600 !important;
  }
  
  .insight-content span[style*="background:#EDF1F8"] {
    background-color: #EDF1F8 !important;
    padding: 0 6px !important;
    border-radius: 6px !important;
    color: #242424 !important;
  }
  
  @keyframes typing {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
  
  @keyframes blink {
    0%, 50% {
      border-color: transparent;
    }
    51%, 100% {
      border-color: #333;
    }
  }
  
  .typing-animation {
    overflow: hidden;
    white-space: nowrap;
    border-right: 2px solid #333;
    animation: typing 2s steps(40, end), blink 1s infinite;
  }
  
  .typing-complete {
    white-space: normal;
    border-right: none;
  }
  
  .line-typing {
    overflow: hidden;
    white-space: nowrap;
    animation: typing 1.5s steps(30, end);
    transform: translateZ(0); /* Prevent jumping */
    will-change: transform, opacity; /* Optimize for animations */
    backface-visibility: hidden; /* Prevent flickering */
  }
  
  .line-complete {
    white-space: normal;
    border-right: none;
    animation: fadeIn 0.3s ease-in-out; /* Smooth fade in when complete */
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0.8;
    }
    to {
      opacity: 1;
    }
  }

   .message-content {
    word-wrap: break-word;
    overflow-wrap: break-word;
    width: 100%;
    min-width: 0;
  }

  @media (max-width: 768px) {
  .chart-container {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: auto;
  }
  
  /* Force chart elements to be responsive */
  canvas[role="img"], .recharts-surface {
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
  }
}
  
`;

interface ChatMessageComponentProps {
	message: ChatMessage;
	onFeedback?: (messageId: string, type: 'thumbs_up' | 'thumbs_down') => void;
	onVisualizationChange?: (messageId: string, type: string, options?: any) => void;
	onFilterUpdateComplete?: (messageId: string, updatedData: any) => void;
	onFilterDataChange?: (messageId: string, filterData: FilterItem[]) => void;
	onMessageEdit?: (messageId: string, newContent: string) => void;
	onRegenerateInsights?: (messageId: string, originalPrompt: string) => void;
}

function ChatMessageComponent({
	message,
	onFeedback,
	onVisualizationChange,
	onFilterUpdateComplete,
	onFilterDataChange,
	onMessageEdit,
	onRegenerateInsights,
}: ChatMessageComponentProps) {
	const { activeConversationId } = useChat();
	const [showDetails, setShowDetails] = useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isUnderlyingDataOpen, setIsUnderlyingDataOpen] = useState(false);

	const [currentVisualizationType, setCurrentVisualizationType] = useState(message.visualization?.type || '');
	const [isAnimating, setIsAnimating] = useState(false);
	const [shouldRender, setShouldRender] = useState(false);
	const [insightsLoading, setInsightsLoading] = useState(false);
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [visibleInsights, setVisibleInsights] = useState<{
		summary: boolean;
		dimension: boolean;
		time: boolean;
		report: boolean;
		buttons: boolean;
	}>({
		summary: false,
		dimension: false,
		time: false,
		report: false,
		buttons: false,
	});
	const [lineTypingStates, setLineTypingStates] = useState<{
		summary: number[]; // Array of line indices currently typing
		dimension: number[];
		time: number[];
		report: number[];
	}>({
		summary: [],
		dimension: [],
		time: [],
		report: [],
	});
	const [lineVisibilityStates, setLineVisibilityStates] = useState<{
		summary: number[]; // Array of line indices that should be visible
		dimension: number[];
		time: number[];
		report: number[];
	}>({
		summary: [],
		dimension: [],
		time: [],
		report: [],
	});
	const [isEditing, setIsEditing] = useState(false);
	const [editedContent, setEditedContent] = useState(message.content || '');
	const [showHoverButtons, setShowHoverButtons] = useState(false);

	// Ref to store timeout IDs for cleanup
	const timeoutRefs = useRef<{
		summary: number[];
		dimension: number[];
		time: number[];
		report: number[];
	}>({
		summary: [],
		dimension: [],
		time: [],
		report: [],
	});
	const [isCopied, setIsCopied] = useState(false);
	const tableRef = useRef<TableViewRef>(null);
	const animationTriggeredRef = useRef(false);
	const initialLoadRef = useRef(true);

	//console.log('Rendering ChatMessageComponent for message ID:', message.message_id, 'with visualization Message:', message);

	// Inject CSS styles
	React.useEffect(() => {
		const styleElement = document.createElement('style');
		styleElement.textContent = animationStyles;
		document.head.appendChild(styleElement);

		return () => {
			document.head.removeChild(styleElement);
		};
	}, []);

	// Reset visualization type when message changes (e.g., after edit)
	React.useEffect(() => {
		// Reset to the message's default visualization type
		setCurrentVisualizationType(message.visualization?.type || '');

		// Reset insights states when message changes
		setVisibleInsights({ summary: false, dimension: false, time: false, report: false, buttons: false });
		setLineTypingStates({ summary: [], dimension: [], time: [], report: [] });
		setLineVisibilityStates({ summary: [], dimension: [], time: [], report: [] });

		// Reset animation trigger
		animationTriggeredRef.current = false;
	}, [message.message_id, message.visualization?.type]);

	// Handle show/hide animations
	React.useEffect(() => {
		if (showDetails) {
			setShouldRender(true);
			setIsAnimating(true);
		} else {
			setIsAnimating(false);
			const timer = setTimeout(() => {
				setShouldRender(false);
			}, 300); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [showDetails]);

	// Handle insights loading and typing animation
	React.useEffect(() => {
		// Check if we have any meaningful insights (not empty strings)
		const hasSummaryInsight =
			message.insights?.summary_insights &&
			typeof message.insights.summary_insights === 'string' &&
			message.insights.summary_insights.trim() !== '';
		const hasDimensionInsight =
			message.insights?.dimension_insights &&
			typeof message.insights.dimension_insights === 'string' &&
			message.insights.dimension_insights.trim() !== '';
		const hasTimeInsight =
			message.insights?.time_insights &&
			typeof message.insights.time_insights === 'string' &&
			message.insights.time_insights.trim() !== '';
		const hasReportInsight =
			message.insights?.report_insights &&
			Array.isArray(message.insights.report_insights) &&
			message.insights.report_insights.length > 0;

		const hasAnyInsight = hasSummaryInsight || hasDimensionInsight || hasTimeInsight || hasReportInsight;

		// Prevent multiple animation triggers with debounce
		if (hasAnyInsight && !animationTriggeredRef.current) {
			animationTriggeredRef.current = true;
			if (!isFilterLoading) {
				setInsightsLoading(false);
			}

			// Define timing constants - no starting delay
			const baseDelay = 0; // No starting delay
			const maxDelay = 500; // Minimal maximum delay

			// Clear any existing timeouts before resetting states
			Object.values(timeoutRefs.current).forEach((timeouts) => {
				timeouts.forEach((timeout) => clearTimeout(timeout as any));
			});
			timeoutRefs.current = {
				summary: [],
				dimension: [],
				time: [],
				report: [],
			};

			// Only reset states if this is the first time we're processing insights
			setVisibleInsights({ summary: false, dimension: false, time: false, report: false, buttons: false });
			setLineTypingStates({ summary: [], dimension: [], time: [], report: [] });
			setLineVisibilityStates({ summary: [], dimension: [], time: [], report: [] });

			// Start sequential typing animation for available insights
			if (hasSummaryInsight && message.insights?.summary_insights) {
				// Show and start summary insights immediately
				setVisibleInsights((prev) => ({ ...prev, summary: true }));
				startLineByLineTyping('summary', message.insights.summary_insights);

				// Calculate total time for summary with improved timing
				const summaryLines = message.insights.summary_insights
					.split(/<br\s*\/?>/i)
					.filter((line) => line.trim());
				const lineDelay = Math.min(baseDelay + summaryLines.length * 20, maxDelay);
				// Calculate actual typing time for each line - much faster
				const summaryTypingTime = summaryLines.reduce((total, line) => {
					const lineLength = line.replace(/<[^>]*>/g, '').length;
					const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
					return total + typingDuration;
				}, 0);
				const summaryTotalTime = (summaryLines.length - 1) * lineDelay + summaryTypingTime + 200; // Much smaller buffer

				// After summary finishes, start dimension insights
				setTimeout(() => {
					if (hasDimensionInsight && message.insights?.dimension_insights) {
						setVisibleInsights((prev) => ({ ...prev, dimension: true }));
						startLineByLineTyping('dimension', message.insights.dimension_insights);

						// Calculate total time for dimension with improved timing
						const dimensionLines = message.insights.dimension_insights
							.split(/<br\s*\/?>/i)
							.filter((line) => line.trim());
						const dimensionLineDelay = Math.min(baseDelay + dimensionLines.length * 20, maxDelay);
						// Calculate actual typing time for each line - much faster
						const dimensionTypingTime = dimensionLines.reduce((total, line) => {
							const lineLength = line.replace(/<[^>]*>/g, '').length;
							const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
							return total + typingDuration;
						}, 0);
						const dimensionTotalTime =
							(dimensionLines.length - 1) * dimensionLineDelay + dimensionTypingTime + 200; // Much smaller buffer

						// After dimension finishes, start time insights
						setTimeout(() => {
							if (hasTimeInsight && message.insights?.time_insights) {
								const timeInsights = message.insights.time_insights;
								setVisibleInsights((prev) => ({ ...prev, time: true }));
								startLineByLineTyping('time', timeInsights);

								// Calculate total time for time insights with improved timing
								const timeLines = message.insights.time_insights
									.split(/<br\s*\/?>/i)
									.filter((line) => line.trim());
								const timeLineDelay = Math.min(baseDelay + timeLines.length * 20, maxDelay);
								// Calculate actual typing time for each line - much faster
								const timeTypingTime = timeLines.reduce((total, line) => {
									const lineLength = line.replace(/<[^>]*>/g, '').length;
									const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
									return total + typingDuration;
								}, 0);
								const timeTotalTime = (timeLines.length - 1) * timeLineDelay + timeTypingTime + 200; // Much smaller buffer

								// After time insights finish, show reports and buttons
								setTimeout(() => {
									if (hasReportInsight) {
										setVisibleInsights((prev) => ({ ...prev, report: true }));
									}
									// Always show buttons after insights are complete
									setVisibleInsights((prev) => ({ ...prev, buttons: true }));
								}, timeTotalTime);
							} else {
								// If no time insights, show reports and buttons after dimension
								setTimeout(() => {
									if (hasReportInsight) {
										setVisibleInsights((prev) => ({ ...prev, report: true }));
									}
									// Always show buttons after insights are complete
									setVisibleInsights((prev) => ({ ...prev, buttons: true }));
								}, dimensionTotalTime);
							}
						}, dimensionTotalTime);
					} else if (hasTimeInsight && message.insights?.time_insights) {
						// If no dimension insights, go directly to time insights
						const timeInsights = message.insights.time_insights;
						setTimeout(() => {
							setVisibleInsights((prev) => ({ ...prev, time: true }));
							startLineByLineTyping('time', timeInsights);

							// Calculate total time for time insights with improved timing
							const timeLines =
								message.insights?.time_insights?.split(/<br\s*\/?>/i).filter((line) => line.trim()) ||
								[];
							const timeLineDelay = Math.min(baseDelay + timeLines.length * 20, maxDelay);
							// Calculate actual typing time for each line - much faster
							const timeTypingTime = timeLines.reduce((total, line) => {
								const lineLength = line.replace(/<[^>]*>/g, '').length;
								const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
								return total + typingDuration;
							}, 0);
							const timeTotalTime = (timeLines.length - 1) * timeLineDelay + timeTypingTime + 200; // Much smaller buffer

							// After time insights finish, show reports and buttons
							setTimeout(() => {
								if (hasReportInsight) {
									setVisibleInsights((prev) => ({ ...prev, report: true }));
								}
								// Always show buttons after insights are complete
								setVisibleInsights((prev) => ({ ...prev, buttons: true }));
							}, timeTotalTime);
						}, summaryTotalTime);
					}
				}, summaryTotalTime);
			} else if (hasDimensionInsight && message.insights?.dimension_insights) {
				// If no summary insights, start with dimension
				setVisibleInsights((prev) => ({ ...prev, dimension: true }));
				startLineByLineTyping('dimension', message.insights.dimension_insights);

				const dimensionLines = message.insights.dimension_insights
					.split(/<br\s*\/?>/i)
					.filter((line) => line.trim());
				const dimensionLineDelay = Math.min(baseDelay + dimensionLines.length * 20, maxDelay);
				// Calculate actual typing time for each line - much faster
				const dimensionTypingTime = dimensionLines.reduce((total, line) => {
					const lineLength = line.replace(/<[^>]*>/g, '').length;
					const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
					return total + typingDuration;
				}, 0);
				const dimensionTotalTime = (dimensionLines.length - 1) * dimensionLineDelay + dimensionTypingTime + 200; // Much smaller buffer

				setTimeout(() => {
					if (hasTimeInsight && message.insights?.time_insights) {
						const timeInsights = message.insights.time_insights;
						setVisibleInsights((prev) => ({ ...prev, time: true }));
						startLineByLineTyping('time', timeInsights);

						// Calculate total time for time insights with improved timing
						const timeLines =
							message.insights?.time_insights?.split(/<br\s*\/?>/i).filter((line) => line.trim()) || [];
						const timeLineDelay = Math.min(baseDelay + timeLines.length * 20, maxDelay);
						// Calculate actual typing time for each line - much faster
						const timeTypingTime = timeLines.reduce((total, line) => {
							const lineLength = line.replace(/<[^>]*>/g, '').length;
							const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
							return total + typingDuration;
						}, 0);
						const timeTotalTime = (timeLines.length - 1) * timeLineDelay + timeTypingTime + 200; // Much smaller buffer

						// After time insights finish, show reports and buttons
						setTimeout(() => {
							if (hasReportInsight) {
								setVisibleInsights((prev) => ({ ...prev, report: true }));
							}
							// Always show buttons after insights are complete
							setVisibleInsights((prev) => ({ ...prev, buttons: true }));
						}, timeTotalTime);
					} else {
						// If no time insights, show reports and buttons after dimension
						setTimeout(() => {
							if (hasReportInsight) {
								setVisibleInsights((prev) => ({ ...prev, report: true }));
							}
							// Always show buttons after insights are complete
							setVisibleInsights((prev) => ({ ...prev, buttons: true }));
						}, dimensionTotalTime);
					}
				}, dimensionTotalTime);
			} else if (hasTimeInsight && message.insights?.time_insights) {
				// If only time insights, start with time
				setVisibleInsights((prev) => ({ ...prev, time: true }));
				startLineByLineTyping('time', message.insights.time_insights);

				// Calculate total time for time insights with improved timing
				const timeLines =
					message.insights?.time_insights?.split(/<br\s*\/?>/i).filter((line) => line.trim()) || [];
				const timeLineDelay = Math.min(baseDelay + timeLines.length * 20, maxDelay);
				// Calculate actual typing time for each line - much faster
				const timeTypingTime = timeLines.reduce((total, line) => {
					const lineLength = line.replace(/<[^>]*>/g, '').length;
					const typingDuration = Math.max(200, Math.min(600, lineLength * 15));
					return total + typingDuration;
				}, 0);
				const timeTotalTime = (timeLines.length - 1) * timeLineDelay + timeTypingTime + 200; // Much smaller buffer

				// After time insights finish, show reports and buttons
				setTimeout(() => {
					if (hasReportInsight) {
						setVisibleInsights((prev) => ({ ...prev, report: true }));
					}
					// Always show buttons after insights are complete
					setVisibleInsights((prev) => ({ ...prev, buttons: true }));
				}, timeTotalTime);
			} else if (hasReportInsight) {
				// If only report insights, show them immediately
				setVisibleInsights((prev) => ({ ...prev, report: true }));
				// Always show buttons after insights are complete
				setVisibleInsights((prev) => ({ ...prev, buttons: true }));
			} else {
				// If no insights but we have buttons, show them
				setVisibleInsights((prev) => ({ ...prev, buttons: true }));
			}
		} else if (hasAnyInsight && animationTriggeredRef.current) {
			// Animation already started, just handle new insights that arrived
			if (hasSummaryInsight && !visibleInsights.summary && message.insights?.summary_insights) {
				setVisibleInsights((prev) => ({ ...prev, summary: true }));
				startLineByLineTyping('summary', message.insights.summary_insights);
			}
			if (hasDimensionInsight && !visibleInsights.dimension && message.insights?.dimension_insights) {
				setVisibleInsights((prev) => ({ ...prev, dimension: true }));
				startLineByLineTyping('dimension', message.insights.dimension_insights);
			}
			if (hasTimeInsight && !visibleInsights.time && message.insights?.time_insights) {
				setVisibleInsights((prev) => ({ ...prev, time: true }));
				startLineByLineTyping('time', message.insights.time_insights);
			}
		} else {
			if (!isFilterLoading || !hasAnyInsight) {
				setInsightsLoading(false);
			}
		}

		if (hasReportInsight && !visibleInsights.report && !isUser && !isPending) {
			setVisibleInsights((prev) => ({ ...prev, report: true, buttons: true }));
		}

		return () => {
			animationTriggeredRef.current = false;
		};
	}, [message.insights?.summary_insights, message.insights?.dimension_insights, message.insights?.time_insights, isFilterLoading]);

	const isUser = message.role === 'user';
	const isPending = message.pending ?? false;

	// Trigger insights loading when message is complete (not pending)
	React.useEffect(() => {
		if (!isUser && !isPending && !message.insights) {
			setInsightsLoading(true);
			initialLoadRef.current = false;
		} else if (message.insights) {
			// Mark that we've processed at least one response
			initialLoadRef.current = false;
		}
	}, [isUser, isPending, message.insights]);

	// Additional fallback outside useEffect to ensure report insights show
	useEffect(() => {
		const hasReportInsight =
			message.insights?.report_insights &&
			Array.isArray(message.insights.report_insights) &&
			message.insights.report_insights.length > 0;

		if (hasReportInsight && !visibleInsights.report && !isUser && !isPending) {
			setVisibleInsights((prev) => ({ ...prev, report: true, buttons: true }));
		}
	}, [message.insights?.report_insights, visibleInsights.report, isUser, isPending]);

	// Cleanup timeouts on unmount or when insights change
	useEffect(() => {
		return () => {
			// Clear all timeouts when component unmounts or insights change
			Object.values(timeoutRefs.current).forEach((timeouts) => {
				timeouts.forEach((timeout) => clearTimeout(timeout as any));
			});
			// Reset timeout arrays
			timeoutRefs.current = {
				summary: [],
				dimension: [],
				time: [],
				report: [],
			};
		};
	}, [message.insights?.summary_insights, message.insights?.dimension_insights, message.insights?.time_insights]);

	const isError = message.error;

	const hasVisualization = !!(message.visualization && message.visualization.type !== 'text');

	const handleEditClick = () => {
		setIsEditing(true);
		setEditedContent(message.content || '');
	};

	const handleCancelEdit = () => {
		setIsEditing(false);
		setEditedContent(message.content || '');
	};

	const handleSendEdit = () => {
		if (editedContent.trim() !== '') {
			if (onMessageEdit) {
				onMessageEdit(message.message_id, editedContent.trim());
			}
			setIsEditing(false);
		}
	};

	const handleRegenerateInsights = () => {
		// Call the API directly for regenerate without changing store
		if (onRegenerateInsights) {
			onRegenerateInsights(message.message_id, '');
		}
	};

	const handleMouseEnter = () => {
		if (isUser && !isPending && !isEditing) {
			setShowHoverButtons(true);
		}
	};

	const handleMouseLeave = () => {
		setShowHoverButtons(false);
	};

	const startLineByLineTyping = (insightType: 'summary' | 'dimension' | 'time', content: string) => {
		// Ensure content is a string and not empty
		if (!content || typeof content !== 'string') {
			console.warn(`Invalid content for ${insightType} insights:`, content);
			return;
		}

		// Clear any existing timeouts for this insight type
		if (timeoutRefs.current[insightType]) {
			timeoutRefs.current[insightType].forEach((timeout) => clearTimeout(timeout as any));
			timeoutRefs.current[insightType] = [];
		}

		const lines = content.split(/<br\s*\/?>/i).filter((line) => line.trim());

		// Calculate dynamic timing based on content length - no starting delay
		const baseDelay = 0; // No starting delay
		const maxDelay = 500; // Minimal maximum delay
		const lineDelay = Math.min(baseDelay + lines.length * 20, maxDelay);

		lines.forEach((line, index) => {
			const timeoutId = setTimeout(() => {
				// Make line visible and start typing at exactly the same moment
				setLineVisibilityStates((prev) => ({
					...prev,
					[insightType]: [...prev[insightType], index],
				}));

				setLineTypingStates((prev) => ({
					...prev,
					[insightType]: [...prev[insightType], index],
				}));

				// Calculate typing duration based on line length - much faster
				const lineLength = line.replace(/<[^>]*>/g, '').length; // Remove HTML tags for length calculation
				const typingDuration = Math.max(200, Math.min(600, lineLength * 15)); // 0.2-0.6 seconds based on content

				// Stop typing after animation
				const stopTypingTimeout = setTimeout(() => {
					setLineTypingStates((prev) => ({
						...prev,
						[insightType]: prev[insightType].filter((i) => i !== index),
					}));
				}, typingDuration);

				// Store the stop typing timeout for cleanup
				timeoutRefs.current[insightType].push(stopTypingTimeout as any);
			}, index * lineDelay);

			// Store the main timeout for cleanup
			timeoutRefs.current[insightType].push(timeoutId as any);
		});
	};

	// Function to clean up malformed HTML (fix double single quotes)
	const cleanHtmlContent = (html: string): string => {
		if (!html || typeof html !== 'string') return '';

		let cleaned = html
			.replace(/''/g, "'") // Replace double single quotes with single quotes
			.replace(/style='([^']*)'/g, (match, styleContent) => {
				// Clean up style attributes that might have double quotes
				return `style="${styleContent.replace(/"/g, '&quot;')}"`;
			})
			.replace(/style="([^"]*)"/g, (match, styleContent) => {
				// Ensure proper escaping of quotes in style content
				return `style="${styleContent.replace(/"/g, '&quot;')}"`;
			});

		return cleaned;
	};

	// Component to render insight with line-by-line typing
	const renderInsightWithLineTyping = (content: string, insightType: 'summary' | 'dimension' | 'time') => {
		// Ensure content is a string and not empty
		if (!content || typeof content !== 'string') {
			console.warn(`Invalid content for ${insightType} insights rendering:`, content);
			return null;
		}

		const lines = content.split(/<br\s*\/?>/i);

		return (
			<div className="leading-relaxed mt-3">
				{lines.map((line, index) => {
					if (!line.trim()) return null;

					const isVisible = lineVisibilityStates[insightType].includes(index);
					const isTyping = lineTypingStates[insightType].includes(index);

					// Only render if the line is visible
					if (!isVisible) return null;

					// Clean the HTML content before rendering
					const cleanedLine = cleanHtmlContent(line);

					return (
						<div
							key={index}
							className={`${isTyping ? 'line-typing' : ''}`}
							dangerouslySetInnerHTML={{ __html: cleanedLine }}
						/>
					);
				})}
			</div>
		);
	};

	const handleFeedback = (type: 'thumbs_up' | 'thumbs_down') => {
		if (onFeedback && !isUser) {
			onFeedback(message.message_id, type);
		}
	};

	const handleOpenDrawer = () => {
		setIsDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	const handleOpenUnderlyingData = () => {
		setIsUnderlyingDataOpen(true);
	};

	const handleCloseUnderlyingData = () => {
		setIsUnderlyingDataOpen(false);
	};

	const handleVisualizationTypeChange = (newType: string) => {
		setCurrentVisualizationType(newType);
		if (onVisualizationChange) {
			onVisualizationChange(message.message_id, newType);
		}
	};

	const handleFilterUpdateComplete = (updatedData: any) => {
		if (onFilterUpdateComplete) {
			onFilterUpdateComplete(message.message_id, updatedData);
		}
	};

	// Use ref to persist filter data across re-renders and scenarios
	const currentFilterDataRef = useRef<FilterItem[]>([]);
	const [currentFilterData, setCurrentFilterData] = useState<FilterItem[]>([]);

	// Initialize filter data - start with empty array, will be updated when filters are applied
	useEffect(() => {
		// Filter data will be updated through handleFilterDataChange when filters are applied
	}, []);

	const handleFilterDataChange = (filterData: FilterItem[]) => {
		// Update both ref and state
		currentFilterDataRef.current = filterData;
		setCurrentFilterData(filterData);
		if (onFilterDataChange) {
			onFilterDataChange(message.message_id, filterData);
		}
	};

	const handleInsightsLoadingChange = (isLoading: boolean) => {
		setIsFilterLoading(isLoading);
		setInsightsLoading(isLoading);
	};

	// Get current filter data (always use ref for latest value)
	const getCurrentFilterData = () => currentFilterDataRef.current;

	// Table action handlers
	const handleExportExcel = () => {
		if (tableRef.current) {
			tableRef.current.handleExport();
		}
	};

	const handleDownloadPDF = () => {
		// Always use the latest filter data from ref
		const latestFilterData = getCurrentFilterData();
		if (tableRef.current) {
			tableRef.current.handlePDFDownload();
		}
	};

	const handleFullView = () => {
		if (tableRef.current) {
			tableRef.current.handleFullView();
		}
	};

	const isTableVisualization = hasVisualization && message.visualization?.type === 'table';

	const isChartVisualization =
		hasVisualization &&
		[
			'barchart',
			'linechart',
			'piechart',
			'dashboard',
			'map',
			'gauge',
			'treemap',
			'timeline',
			'sankey',
			'columnchart',
		].includes(currentVisualizationType);

	const summaryInsightContent = message.insights?.summary_insights;
	const dimensionInsightContent = message.insights?.dimension_insights;
	const timeInsightContent = message.insights?.time_insights;
	const reportInsightContent = message.insights?.report_insights;

	const hasRenderableInsights =
		(typeof summaryInsightContent === 'string' && summaryInsightContent.trim() !== '') ||
		(typeof dimensionInsightContent === 'string' && dimensionInsightContent.trim() !== '') ||
		(typeof timeInsightContent === 'string' && timeInsightContent.trim() !== '') ||
		(Array.isArray(reportInsightContent) && reportInsightContent.length > 0);

	const shouldShowInsightsBlock = !isUser && !isPending && (insightsLoading || hasRenderableInsights);

	useEffect(() => {
		if (isUser || isPending) {
			return;
		}

		const shouldShowButtons = hasRenderableInsights || (!insightsLoading && !!message.insights);

		if (shouldShowButtons && !visibleInsights.buttons) {
			setVisibleInsights((prev) => ({ ...prev, buttons: true }));
		}
	}, [isUser, isPending, hasRenderableInsights, insightsLoading, message.insights, visibleInsights.buttons]);

	const formatSQLQuery = (sql: string) => {
		try {
			const formattedSQL = format(sql, {
				language: 'sql',
				tabWidth: 2,
				useTabs: false,
				keywordCase: 'upper',
				linesBetweenQueries: 1,
			});

			return (
				<SyntaxHighlighter
					language="sql"
					style={coy}
					customStyle={{
						margin: 0,
						fontSize: '0.75rem',
						lineHeight: '1.4',
						borderRadius: '0.375rem',
						backgroundColor: 'transparent',
					}}
					codeTagProps={{
    style: { padding: 0 }, 
  }}
					showLineNumbers={false}
					wrapLines={true}
				>
					{formattedSQL}
				</SyntaxHighlighter>
			);
		} catch (error) {
			// Fallback to original SQL if formatting fails
			return <span className="text-gray-800">{sql}</span>;
		}
	};

	// Function to copy message content with proper formatting (like ChatGPT)
	const copyMessageContent = () => {
		let contentToCopy = '';

		// Add main message content
		if (message.content) {
			contentToCopy += message.content + '\n\n';
		}

		// Add insights with proper formatting
		if (message.insights) {
			// Add summary insights
			if (message.insights.summary_insights) {
				contentToCopy += message.insights.summary_insights + '\n\n';
			}

			// Add dimension insights
			if (message.insights.dimension_insights) {
				contentToCopy += message.insights.dimension_insights + '\n\n';
			}

			// Add time insights
			if (message.insights.time_insights) {
				contentToCopy += message.insights.time_insights;
			}
		}

		// Copy HTML content to clipboard (preserves formatting)
		const htmlContent = contentToCopy.trim();

		// Create a temporary element to get the HTML
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = htmlContent;

		// Copy both HTML and plain text
		const clipboardData = new ClipboardItem({
			'text/html': new Blob([htmlContent], { type: 'text/html' }),
			'text/plain': new Blob([tempDiv.textContent || ''], { type: 'text/plain' }),
		});

		navigator.clipboard
			.write([clipboardData])
			.then(() => {
				// Show checkmark for 2 seconds
				setIsCopied(true);
				setTimeout(() => {
					setIsCopied(false);
				}, 2000);
			})
			.catch((err) => {
				// Fallback to plain text if HTML copy fails
				navigator.clipboard
					.writeText(tempDiv.textContent || '')
					.then(() => {
						// Show checkmark for 2 seconds
						setIsCopied(true);
						setTimeout(() => {
							setIsCopied(false);
						}, 2000);
					})
					.catch((fallbackErr) => {
						console.error('Failed to copy content: ', fallbackErr);
					});
			});
	};

	return (
		<>
			<div 
				className={`flex ${isUser ? 'justify-end' : 'justify-start'} relative w-full max-w-full group`}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div
					className={`relative
            	${isChartVisualization || isTableVisualization ? 'w-full max-w-none' : 'max-w-[600px] md:max-w-[90%]'}
			${isEditing ? 'w-full max-w-[600px]' : ''}
            rounded-[30px] 
            text-body
			mb-10
            ${isUser ? 'bg-secondary-background text-black px-4 py-1.5' : 'bg-[#fff] text-black'} 
            ${isError ? 'border-Red border' : ''}
			${isEditing ? ' p-4' : ''}
          `}
				>
					{isPending ? (
						<LoadingIndicator size="md" />
					) : (
						<div>
							{isEditing ? (
								<div className="space-y-4 text-lg py-1">
									<textarea
										value={editedContent}
										onChange={(e) => setEditedContent(e.target.value)}
										className="w-full resize-none bg-secondary-background focus:outline-none"
										rows={3}
										placeholder="Type your message..."
									/>
									<div className="flex justify-end space-x-2">
										<button
											onClick={handleCancelEdit}
											className="px-4 py-2 text-tertiary-text bg-white rounded-full transition-colors text-ui"
										>
											Cancel
										</button>
										<button
											onClick={handleSendEdit}
											disabled={!editedContent.trim()}
											className="px-6 py-2 bg-primary text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-ui"
										>
											Send
										</button>
									</div>
								</div>
							) : (
								<>
									{!isUser && message.content && message.content ? (
										<MessageContent content={message.content} />
									) : (
										message.content && (
											<div className="whitespace-pre-wrap text-lg text-primary-text">
												{message.content}
											</div>
										)
									)}

								    {isUser && !isPending && !isEditing && showHoverButtons && (
											<div
												className="absolute right-4 top-3/4 transform -translate-y-1/2 
												flex space-x-1 duration-200 mt-7 opacity-100 transition-opacity"
											>
												<Tooltip message={isCopied ? 'Copied!' : 'Copy message'}>
													<button
														title={isCopied ? 'Copied!' : 'Copy message'}
														onClick={() => copyMessageContent()}
														className="p-2 text-tertiary-text hover:text-blue-600 
													hover:bg-secondary-background rounded-full transition-colors"
													>
														{isCopied ? <Check className="w-4 h-4" strokeWidth={1.75} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
													</button>
												</Tooltip>

												<Tooltip message="Edit message">
													<button
														title="Edit message"
														onClick={handleEditClick}
														className="p-2 text-tertiary-text hover:text-blue-600 
													hover:bg-secondary-background rounded-full transition-colors"
													>
														<FiEdit2 className="w-4 h-4" strokeWidth={1.75}/>
													</button>
												</Tooltip>
											</div>
										)}

									{!isUser && (hasVisualization || isPending) && (
										<div
											className={`
                  
											relative
											${
												message.visualization?.type === 'table'
													? 'p-3 bg-White border border-PeriwinkleGray rounded-[28px] max-w-[100%]'
													: ''
											}
											${isChartVisualization ? 'p-3 bg-White rounded-[28px] border border-PeriwinkleGray overflow-x-auto' : ''}
											${message.visualization?.type === 'text' ? 'p-3 bg-White rounded-md border border-PeriwinkleGray' : ''}
											${isPending && !hasVisualization ? 'p-3 bg-White rounded-[28px] border border-PeriwinkleGray' : ''}
											`}
										>
											{isPending && !hasVisualization ? (
												<div className="flex items-center justify-center py-8">
													<div className="flex items-center space-x-2 text-gray-600">
														<LoadingIndicator size="md" />
														<span>Loading visualization...</span>
													</div>
												</div>
											) : (
												<>
													{(isChartVisualization && currentVisualizationType != 'linechart')  && (
														<ChartSwitcher
															defaultType={message.visualization?.type}
															currentType={currentVisualizationType}
															data={message.visualization?.data || []}
															onTypeChange={handleVisualizationTypeChange}
														/>
													)}

													<div
														className={isChartVisualization ? 'w-full max-w-full overflow-hidden' : 'w-full max-w-[856px]'}
													>
														<div className="flex justify-end">
															<AppliedFiltersDisplay
																sqlQuery={message.sql_query}
																onFilterUpdateComplete={handleFilterUpdateComplete}
																onFilterDataChange={handleFilterDataChange}
																filterData={currentFilterData}
																messageId={message.message_id}
																hasChartSwitcher={isChartVisualization}
																pdfGeneration={message.metadata?.pdf_generation || false}
																onExportExcel={handleExportExcel}
																onDownloadPDF={handleDownloadPDF}
																onFullView={handleFullView}
																chartData={message.visualization?.data}
																chartType={isChartVisualization ? currentVisualizationType : (isTableVisualization ? 'table' : undefined)}
																chartMetadata={message.metadata}
																onInsightsLoadingChange={handleInsightsLoadingChange}
																// Table-specific props
																tableData={isTableVisualization ? {
																	data: message.visualization?.data,
																	sortConfig: null,
																	onSort: () => {},
																	filteredAndSortedRows: message.visualization?.data?.rows || [],
																	isNumericColumn: () => false,
																	formatCellValue: (cell: any) => cell,
																	onDataUpdate: () => {}
																} : undefined}
																tableRequest={isTableVisualization ? {
																	sql_query: message.sql_query,
																	page_size: 50,
																	page: 1
																} : undefined}
																onTableFullView={handleFullView}
															/>
														</div>
														<div
															className={
																isChartVisualization ? 'min-w-[320px] w-full' : 'w-full'
															}
														>
															<Visualization
																key={`${message.message_id}-${
																	message.visualization?.type
																}-${isPending ? 'pending' : 'complete'}`}
																ref={tableRef}
																type={currentVisualizationType}
																data={message.visualization?.data || []}
																messageId={message.message_id}
																sqlquery={message.sql_query}
																metadata={message.metadata}
																onFilterUpdateComplete={handleFilterUpdateComplete}
																filterData={currentFilterData}
																onExportExcel={handleExportExcel}
																onDownloadPDF={handleDownloadPDF}
																onFullView={handleFullView}
															/>
														</div>
													</div>
												</>
											)}
										</div>
									)}

									{shouldShowInsightsBlock && (
										<div className="flex items-center justify-start w-full">
											<div className="bg-white max-w-[856px] px-3 md:px-[12px] pt-[32px] pb-[16px]">
												<div className="text-body leading-relaxed text-primary-text">
													{insightsLoading && (
														<div className="flex items-center space-x-2 text-gray-600">
															<LoadingIndicator size="sm" />
															<span>Analyzing insights...</span>
														</div>
													)}

													{/* Summary insights with line-by-line typing - show only when visible */}
													{message.insights?.summary_insights &&
														typeof message.insights.summary_insights === 'string' &&
														message.insights.summary_insights.trim() !== '' &&
														visibleInsights.summary &&
														renderInsightWithLineTyping(
															message.insights.summary_insights,
															'summary'
														)}

													{/* Dimension insights with line-by-line typing - show only when visible */}
													{message.insights?.dimension_insights &&
														typeof message.insights.dimension_insights === 'string' &&
														message.insights.dimension_insights.trim() !== '' &&
														visibleInsights.dimension &&
														renderInsightWithLineTyping(
															message.insights.dimension_insights,
															'dimension'
														)}

													{/* Time insights with line-by-line typing - show only when visible */}
													{message.insights?.time_insights &&
														typeof message.insights.time_insights === 'string' &&
														message.insights.time_insights.trim() !== '' &&
														visibleInsights.time &&
														renderInsightWithLineTyping(
															message.insights.time_insights,
															'time'
														)}
												</div>
											</div>
										</div>
									)}

									{!isUser &&
										!isPending &&
										visibleInsights.report &&
										message.insights?.report_insights &&
										message.insights.report_insights.length > 0 && (
											<div className="grid lg:grid-cols-3 grid-cols-1 gap-3 md:gap-4 mx-auto pb-2 max-w-5xl">
												{message.insights?.report_insights?.map(
													(insight: any, index: number) => (
														<a
															key={index}
															href={insight.report_url}
															target="_blank"
															rel="noopener noreferrer"
															title={`Open report: ${insight.report_name}`}
															className="flex items-center gap-2 bg-[#F5F8FA] p-2 rounded-[36px] border border-[#F5F8FA] cursor-pointer min-w-64 hover:bg-[#E8F0F5] transition-colors duration-200 no-underline"
														>
															<div className="flex-shrink-0">
																<ExternalLink
																	style={{ color: '#242424' }}
																	className="w-4 h-4 flex-shrink-0" strokeWidth={1.75}
																/>
															</div>
															<span className="text-gray-700 text-caption block truncate">
																{insight.report_name}
															</span>
														</a>
													)
												)}

												{/* <div className="flex items-center gap-2 bg-[#F5F8FA] p-2 rounded-[36px] border border-[#F5F8FA] cursor-pointer min-w-64">
										<div className="pt-0.5 pl-0.5 flex-shrink-0">
											<ExternalLink
												size={16}
												style={{ color: '#242424' }}
												className="flex-shrink-0"
											/>
										</div>
										<span className="text-gray-700 block truncate">
											Understanding Sales Metrics Ove...
										</span>
									</div>

									
									<div className="flex items-center gap-2 bg-[#F5F8FA] p-2 rounded-[36px] border border-[#F5F8FA] cursor-pointer min-w-64">
										<div className="pt-0.5 pl-0.5 flex-shrink-0">
											<ExternalLink
												size={16}
												style={{ color: '#242424' }}
												className="flex-shrink-0"
											/>
										</div>
										<span className="text-gray-700 truncate">
											Understanding Sales Metrics Ove...
										</span>
									</div> 

									
									<div className="flex items-center gap-2 bg-[#F5F8FA] p-2 rounded-[36px] border border-[#F5F8FA] cursor-pointer min-w-64">
										<div className="pt-0.5 pl-0.5 flex-shrink-0">
											<ExternalLink
												size={16}
												style={{ color: '#242424' }}
												className="flex-shrink-0"
											/>
										</div>
										<span className="text-gray-700 truncate">
											Understanding Sales Metrics Ove...
										</span>
									</div>
									 */}
											</div>
										)}

									{!isUser && message.message_id && visibleInsights.buttons && (
										<div
											className={`flex items-center mt-2 space-x-1 md:space-x-1.5 ${
												isPending ? 'opacity-50' : ''
											}`}
										>
											<Tooltip message={isCopied ? 'Copied!' : 'Copy'}>
												<button
													type="button"
													title={isCopied ? 'Copied!' : 'Copy'}
													onClick={() => copyMessageContent()}
													className="p-2 group flex items-center justify-center rounded-full transition-colors text-tertiary-text hover:bg-gray-100 hover:text-gray-700"
												>
													{isCopied ? (
														<Check className="w-5 h-5" strokeWidth={1.75}/>
													) : (
														<Copy className="w-5 h-5" strokeWidth={1.75}/>
													)}
												</button>
											</Tooltip>

											<Tooltip message="View SQL">
												<button
													title="btnopensqldrawer"
													onClick={() => setShowDetails(!showDetails)}
													type="button"
													className={`p-2 group flex items-center justify-center rounded-full transition-colors
													${
														showDetails
															? 'text-primary'
															: 'text-tertiary-text hover:bg-secondary-background hover:border-tertiary-background hover:text-primary'
													}`}
												>
													<SquareCode className="w-5 h-5" strokeWidth={1.75}/>
												</button>
											</Tooltip>

											{/* Show "View source data" button only if dimension or time insights exist */}
											{((message.insights?.dimension_insights && 
												typeof message.insights.dimension_insights === 'string' && 
												message.insights.dimension_insights.trim() !== '') ||
												(message.insights?.time_insights && 
												typeof message.insights.time_insights === 'string' && 
												message.insights.time_insights.trim() !== '')) && (
												<Tooltip message="View source data">
													<button
														title="btnshowunderlyingdata"
														onClick={handleOpenUnderlyingData}
														type="button"
														className={`p-2 group flex items-center justify-center rounded-full transition-colors
														${isUnderlyingDataOpen ? 'text-primary' : 'text-tertiary-text hover:bg-[#edf1f8] hover:text-primary'}`}
													>
														<Database className="w-5 h-5" strokeWidth={1.75}/>
													</button>
												</Tooltip>
											)}

											<Tooltip message="Chat with the result">
												<button
													title="btnopendrawer"
													onClick={handleOpenDrawer}
													type="button"
													className={`p-2 group flex items-center justify-center rounded-full transition-colors
													${isDrawerOpen ? 'text-primary' : 'text-tertiary-text hover:bg-secondary-background hover:text-primary'}`}
												>
													<MessageSquare className="w-5 h-5" strokeWidth={1.75}/>
												</button>
											</Tooltip>

											<Tooltip message="Good response">
												<button
													title="btngoodresponse"
													type="button"
													onClick={() => handleFeedback('thumbs_up')}
													className={`p-2 group flex items-center justify-center rounded-full transition-colors
                          ${
								message.feedback_type === 'thumbs_up'
									? 'text-green-600'
									: 'text-tertiary-text hover:bg-secondary-background hover:text-primary'
							}`}
												>
													<ThumbsUp className="w-5 h-5" strokeWidth={1.75}/>
												</button>
											</Tooltip>

											<Tooltip message="Bad response">
												<button
													title="btngoodresponse"
													type="button"
													onClick={() => handleFeedback('thumbs_down')}
													className={`p-2 group flex items-center justify-center rounded-full transition-colors
														${
															message.feedback_type === 'thumbs_down'
																? 'text-red-600'
																: 'text-tertiary-text hover:bg-secondary-background hover:text-primary'
														}`}
												>
													<ThumbsDown className="w-5 h-5" strokeWidth={1.75}/>
												</button>
											</Tooltip>

											<Tooltip message="Regenerate">
												<button
													title="btnregenerateoption"
													type="button"
													onClick={handleRegenerateInsights}
													className="p-2 group flex items-center justify-center rounded-full hover:bg-secondary-background text-tertiary-text hover:text-primary transition-colors"
												>
													<RotateCw className="w-5 h-5" strokeWidth={1.75}/>
												</button>
											</Tooltip>
										</div>
									)}

									{/* SQL Query Details */}
									{shouldRender && message.sql_query && (
										<div
											className="mt-2 p-5 bg-[#f8f9fa] rounded-[28px] border border-gray-200 overflow-hidden transition-all duration-300 ease-out"
											style={{
												animation: isAnimating
													? 'slideDown 0.3s ease-out forwards'
													: 'slideUp 0.3s ease-out forwards',
												transformOrigin: 'top',
											}}
										>
											<div className="overflow-x-auto overflow-y-auto max-h-52">
												<pre className="text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
													{formatSQLQuery(message.sql_query)}
												</pre>
											</div>
										</div>
									)}
								</>
							)}
						</div>
					)}
				</div>
		
			</div>

			{/* Inner Chat Drawer */}
			<ChatDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} message={message} />

			{/* Underlying Data Component */}
			<UnderlyingDataComponent
				isOpen={isUnderlyingDataOpen}
				onClose={handleCloseUnderlyingData}
				conversationId={activeConversationId || ''}
				messageId={message.message_id || ''}
			/>
		</>
	);
}

export default ChatMessageComponent;
